import React, { useState, useEffect } from 'react';
import './StandardizedBoxPlotComparison.css';

const StandardizedBoxPlotComparison = ({ data, className }) => {
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'median', 'mean', 'iqr'
  const [showOutliers, setShowOutliers] = useState(true);
  const [showMean, setShowMean] = useState(true);

  // Subject color mapping
  const subjectColors = {
    '中文': '#ef4444',
    '英文': '#3b82f6', 
    '數學': '#eab308',
    '綜合科學': '#10b981',
    '歷史': '#8b5cf6',
    '地理': '#f97316',
    '電腦': '#06b6d4',
    '視覺藝術': '#ec4899',
    '音樂': '#84cc16',
    '體育': '#a855f7'
  };

  // Initialize selected subjects
  useEffect(() => {
    if (data?.subjectOverview) {
      const allSubjects = data.subjectOverview.map(subject => subject.subject);
      setSelectedSubjects(allSubjects);
    }
  }, [data?.subjectOverview]);

  const handleSubjectToggle = (subjectName) => {
    setSelectedSubjects(prev => 
      prev.includes(subjectName) 
        ? prev.filter(s => s !== subjectName)
        : [...prev, subjectName]
    );
  };

  const toggleAllSubjects = () => {
    if (selectedSubjects.length === (data?.subjectOverview?.length || 0)) {
      setSelectedSubjects([]);
    } else {
      const allSubjects = data?.subjectOverview?.map(subject => subject.subject) || [];
      setSelectedSubjects(allSubjects);
    }
  };

  // Filter and sort subjects
  const getValidSubjects = () => {
    if (!data?.subjectOverview) return [];

    return data.subjectOverview
      .filter(subject => selectedSubjects.includes(subject.subject))
      .filter(subject => subject.stats && subject.data && subject.data.length > 0)
      .sort((a, b) => {
        switch (sortBy) {
          case 'median':
            return b.stats.median - a.stats.median;
          case 'mean':
            return b.stats.mean - a.stats.mean;
          case 'iqr':
            return (b.stats.q3 - b.stats.q1) - (a.stats.q3 - a.stats.q1);
          case 'name':
          default:
            return a.subject.localeCompare(b.subject);
        }
      });
  };

  // Calculate global min and max for unified scale
  const getGlobalScale = (subjects) => {
    if (subjects.length === 0) return { min: 0, max: 100, scale: 1 };
    
    const allMins = subjects.map(s => s.stats.min);
    const allMaxs = subjects.map(s => s.stats.max);
    
    const globalMin = Math.min(...allMins);
    const globalMax = Math.max(...allMaxs);
    
    return { min: globalMin, max: globalMax };
  };

  // Render unified horizontal comparison chart
  const renderUnifiedHorizontalChart = (subjects, width = 800, height = 400) => {
    if (subjects.length === 0) return <div className="no-data">無數據 No Data</div>;

    const { min: globalMin, max: globalMax } = getGlobalScale(subjects);
    const margin = { top: 40, right: 60, bottom: 60, left: 120 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    
    const xScale = (value) => margin.left + ((value - globalMin) / (globalMax - globalMin)) * chartWidth;
    const subjectHeight = chartHeight / subjects.length;
    const boxHeight = Math.min(subjectHeight * 0.6, 40);

    return (
      <div className="unified-chart-container">
        <svg width={width} height={height} className="unified-chart-svg">
          {/* Background grid */}
          <defs>
            <pattern id="unified-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f8fafc" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#unified-grid)" />
          
          {/* Y-axis (subject labels) */}
          <line 
            x1={margin.left} y1={margin.top} 
            x2={margin.left} y2={height - margin.bottom} 
            stroke="#e2e8f0" strokeWidth="2"
          />
          
          {/* X-axis (value scale) */}
          <line 
            x1={margin.left} y1={height - margin.bottom} 
            x2={width - margin.right} y2={height - margin.bottom} 
            stroke="#e2e8f0" strokeWidth="2"
          />
          
          {/* X-axis ticks and labels */}
          {[globalMin, (globalMin + globalMax) / 2, globalMax].map((tick, index) => (
            <g key={index}>
              <line 
                x1={xScale(tick)} y1={height - margin.bottom} 
                x2={xScale(tick)} y2={height - margin.bottom + 5} 
                stroke="#64748b" strokeWidth="1"
              />
              <text 
                x={xScale(tick)} y={height - margin.bottom + 20} 
                textAnchor="middle" className="axis-label"
              >
                {tick.toFixed(1)}
              </text>
            </g>
          ))}
          
          {/* Box plots for each subject */}
          {subjects.map((subject, index) => {
            const { min, q1, median, q3, max, outliers, mean } = subject.stats;
            const yCenter = margin.top + index * subjectHeight + subjectHeight / 2;
            const subjectColor = subjectColors[subject.subject] || '#4f46e5';
            
            return (
              <g key={index} className="subject-box-plot">
                {/* Subject label */}
                <text 
                  x={margin.left - 10} y={yCenter + 4} 
                  textAnchor="end" className="subject-label"
                >
                  {subject.subject}
                </text>
                
                {/* Whiskers */}
                <line 
                  x1={xScale(min)} y1={yCenter} 
                  x2={xScale(max)} y2={yCenter} 
                  stroke="#64748b" strokeWidth="1.5"
                />
                
                {/* Box */}
                <rect 
                  x={xScale(q1)} y={yCenter - boxHeight/2} 
                  width={xScale(q3) - xScale(q1)} height={boxHeight} 
                  fill={subjectColor} fillOpacity="0.8" 
                  stroke="#374151" strokeWidth="1.5"
                  rx="2"
                />
                
                {/* Median line */}
                <line 
                  x1={xScale(median)} y1={yCenter - boxHeight/2} 
                  x2={xScale(median)} y2={yCenter + boxHeight/2} 
                  stroke="#ffffff" strokeWidth="2"
                />
                
                {/* Mean marker */}
                {showMean && (
                  <circle 
                    cx={xScale(mean)} cy={yCenter} 
                    r="4" fill="#ffffff" stroke="#374151" strokeWidth="1.5"
                  />
                )}
                
                {/* Outliers */}
                {showOutliers && outliers.map((outlier, outlierIndex) => (
                  <circle 
                    key={outlierIndex}
                    cx={xScale(outlier)} 
                    cy={yCenter + (Math.random() - 0.5) * 30} 
                    r="3" fill="#f59e0b" stroke="#d97706" strokeWidth="1"
                  />
                ))}
              </g>
            );
          })}
          
          {/* Chart title */}
          <text 
            x={width / 2} y={20} 
            textAnchor="middle" className="chart-title"
          >
            科目成績分布比較 Subject Performance Comparison
          </text>
        </svg>
      </div>
    );
  };

  const validSubjects = getValidSubjects();

  if (!data) {
    return (
      <div className="standardized-comparison">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>載入標準化比較數據中... Loading Standardized Comparison Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="standardized-comparison">
      <div className="comparison-header">
        <h2>標準化科目比較 Standardized Subject Comparison</h2>
        <p className="class-info">班級: {className}</p>
        <p className="description">
          統一水平排列的盒鬚圖，便於比較各科目的成績分布變異
        </p>
      </div>

      {/* Controls */}
      <div className="comparison-controls">
        <div className="control-group">
          <h4>科目選擇 Subject Selection</h4>
          <div className="subject-selector">
            <button 
              className="toggle-all-button"
              onClick={toggleAllSubjects}
            >
              {selectedSubjects.length === (data?.subjectOverview?.length || 0) ? '取消全選' : '全選'}
            </button>
            <div className="subject-checkboxes">
              {data?.subjectOverview?.map((subject, index) => (
                <label key={index} className="subject-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedSubjects.includes(subject.subject)}
                    onChange={() => handleSubjectToggle(subject.subject)}
                  />
                  <span 
                    className="subject-color-indicator" 
                    style={{ backgroundColor: subjectColors[subject.subject] || '#4f46e5' }}
                  ></span>
                  <span className="subject-name">{subject.subject}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="control-group">
          <h4>排序方式 Sorting</h4>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="name">按名稱 Name</option>
            <option value="median">按中位數 Median</option>
            <option value="mean">按平均數 Mean</option>
            <option value="iqr">按四分位距 IQR</option>
          </select>
        </div>

        <div className="control-group">
          <h4>顯示選項 Display Options</h4>
          <div className="display-options">
            <label className="option-checkbox">
              <input
                type="checkbox"
                checked={showMean}
                onChange={(e) => setShowMean(e.target.checked)}
              />
              <span>顯示平均數 Show Mean</span>
            </label>
            <label className="option-checkbox">
              <input
                type="checkbox"
                checked={showOutliers}
                onChange={(e) => setShowOutliers(e.target.checked)}
              />
              <span>顯示異常值 Show Outliers</span>
            </label>
          </div>
        </div>
      </div>

      {/* Unified Comparison Chart */}
      <div className="comparison-chart">
        {validSubjects.length === 0 ? (
          <div className="no-data">
            <p>暫無有效的科目數據 No Valid Subject Data</p>
          </div>
        ) : (
          <div className="unified-chart-wrapper">
            {renderUnifiedHorizontalChart(validSubjects, 900, 500)}
            
            {/* Statistics table */}
            <div className="statistics-table">
              <h4>統計摘要 Statistical Summary</h4>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>科目 Subject</th>
                      <th>學生數 Students</th>
                      <th>最小值 Min</th>
                      <th>Q1</th>
                      <th>中位數 Median</th>
                      <th>Q3</th>
                      <th>最大值 Max</th>
                      <th>平均數 Mean</th>
                      <th>IQR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validSubjects.map((subject, index) => (
                      <tr key={index}>
                        <td>
                          <span 
                            className="subject-color-dot" 
                            style={{ backgroundColor: subjectColors[subject.subject] || '#4f46e5' }}
                          ></span>
                          {subject.subject}
                        </td>
                        <td>{subject.data.length}</td>
                        <td>{subject.stats.min.toFixed(1)}</td>
                        <td>{subject.stats.q1.toFixed(1)}</td>
                        <td>{subject.stats.median.toFixed(1)}</td>
                        <td>{subject.stats.q3.toFixed(1)}</td>
                        <td>{subject.stats.max.toFixed(1)}</td>
                        <td>{subject.stats.mean.toFixed(1)}</td>
                        <td>{(subject.stats.q3 - subject.stats.q1).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="comparison-legend">
        <h4>圖例說明 Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color box-color"></div>
            <span>四分位數範圍 (Q1-Q3) Interquartile Range</span>
          </div>
          <div className="legend-item">
            <div className="legend-color median-color"></div>
            <span>中位數 (Median)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color mean-color"></div>
            <span>平均數 (Mean)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color outlier-color"></div>
            <span>異常值 (Outliers)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color whisker-color"></div>
            <span>鬚線範圍 (Whisker Range)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StandardizedBoxPlotComparison;
